import React from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Button,
    Typography
} from '@material-ui/core';
import ExpandMore from '@material-ui/icons/ExpandMore';
import { formatSent } from './formatSent';
import { describeIdentifiers, describeSelectors } from './friendlySelectorDescription';
import RuleDescription from './RuleDescription';
import { SHARING_REQUEST_STATUS, titleStatus } from './sharingRequestModel';
import StatusChip from './StatusChip';
import styles from './VisibilitySharing.module.css';

const EVENT_LABELS = {
    'com.trimble.trans.track.position.event': 'Position',
    'com.trimble.trans.track.location.event': 'Location',
    'com.trimble.trans.track.shipment.event': 'Shipment',
    'com.trimble.trans.track.stopstatus.event': 'Stop Status'
};

const V2SharingGroups = ({ agreement, onDecide }) => {
    const messageTypes = agreement?.messageTypes || {};
    const entries = Object.entries(messageTypes).flatMap(([eventKey, groups]) =>
        (groups || []).map((group, index) => ({
            eventKey,
            group,
            groupIndex: index,
            key: `${eventKey}-${index}`
        }))
    );

    return (
        <>
            <section className={styles.termsPanel}>
                <Typography className={styles.sectionTitle}>Agreement terms</Typography>
                <div className={styles.terms}>
                    <div>
                        <span className={styles.termLabel}>Direction</span>
                        <div className={styles.termValue}>{agreement?.direction || ''}</div>
                    </div>
                    <div>
                        <span className={styles.termLabel}>Valid from</span>
                        <div className={styles.termValue}>{formatSent(agreement?.validFrom)}</div>
                    </div>
                    <div>
                        <span className={styles.termLabel}>Valid to</span>
                        <div className={styles.termValue}>{formatSent(agreement?.validTo)}</div>
                    </div>
                    <div>
                        <span className={styles.termLabel}>Deliver to other parties</span>
                        <div className={styles.termValue}>
                            {agreement?.deliverToOtherParties ? 'Yes' : 'No'}
                        </div>
                    </div>
                </div>
            </section>
            <section className={styles.sharedSection}>
                <h2 className={styles.sharedHeading}>What is being shared</h2>
                {entries.length === 0 ? <Typography>No sharing groups found.</Typography> : null}
                {entries.map(({ eventKey, group, groupIndex, key }) => {
                    const groupStatus =
                        titleStatus(group?.respondedStatus) || SHARING_REQUEST_STATUS.PENDING;
                    const showAccept = groupStatus === SHARING_REQUEST_STATUS.PENDING;
                    const showDecline =
                        groupStatus === SHARING_REQUEST_STATUS.PENDING ||
                        groupStatus === SHARING_REQUEST_STATUS.ACCEPTED;
                    const filters = describeSelectors(group?.selectors);
                    const identifiers = describeIdentifiers(group?.identifiers);
                    const decideTarget = {
                        eventType: eventKey,
                        groupIndex,
                        groupName: group?.groupName
                    };
                    return (
                        <Accordion key={key} square>
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <div className={styles.groupHeader}>
                                    <div className={styles.groupIdentity}>
                                        <Typography className={styles.groupName}>
                                            {group?.groupName || 'Group'} ·{' '}
                                            {EVENT_LABELS[eventKey] || eventKey}
                                        </Typography>
                                        <StatusChip status={groupStatus} />
                                    </div>
                                    {showAccept || showDecline ? (
                                        <div className={styles.actions}>
                                            {showAccept ? (
                                                <Button
                                                    size="small"
                                                    color="primary"
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        onDecide(decideTarget, 'accept');
                                                    }}
                                                >
                                                    Accept
                                                </Button>
                                            ) : null}
                                            {showDecline ? (
                                                <Button
                                                    size="small"
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        onDecide(decideTarget, 'decline');
                                                    }}
                                                >
                                                    Decline
                                                </Button>
                                            ) : null}
                                        </div>
                                    ) : null}
                                </div>
                            </AccordionSummary>
                            <AccordionDetails>
                                <div className={styles.groupBody}>
                                    <div className={styles.groupMeta}>
                                        <div>
                                            <span className={styles.termLabel}>Event type</span>
                                            <div className={styles.termValue}>
                                                {EVENT_LABELS[eventKey] || eventKey}
                                            </div>
                                        </div>
                                        <div>
                                            <span className={styles.termLabel}>Group</span>
                                            <div className={styles.termValue}>
                                                {group?.groupName || 'Group'}
                                            </div>
                                        </div>
                                        <div>
                                            <span className={styles.termLabel}>Status</span>
                                            <div className={styles.termValue}>{groupStatus}</div>
                                        </div>
                                    </div>
                                    {filters.length > 0 ? (
                                        <section className={styles.groupSection}>
                                            <h3 className={styles.groupSectionTitle}>Filters</h3>
                                            {filters.map((filter, index) => (
                                                <RuleDescription
                                                    key={`${key}-filter-${index}`}
                                                    description={filter.description}
                                                    expression={filter.expression}
                                                />
                                            ))}
                                        </section>
                                    ) : null}
                                    {identifiers.length > 0 ? (
                                        <section className={styles.groupSection}>
                                            <h3 className={styles.groupSectionTitle}>
                                                Identifiers
                                            </h3>
                                            {identifiers.map((identifier, index) => (
                                                <RuleDescription
                                                    key={`${key}-identifier-${index}`}
                                                    description={identifier.description}
                                                    expression={identifier.expression}
                                                />
                                            ))}
                                        </section>
                                    ) : null}
                                    {filters.length === 0 && identifiers.length === 0 ? (
                                        <Typography color="textSecondary">
                                            No filters or identifiers on this group.
                                        </Typography>
                                    ) : null}
                                </div>
                            </AccordionDetails>
                        </Accordion>
                    );
                })}
            </section>
        </>
    );
};

export default V2SharingGroups;
